import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { RefreshCw, RotateCcw, Home, AlertTriangle } from "lucide-react";

function Fallback({ error, onRetry, onHome, full }) {
  const detail = String(error?.message || error || "").slice(0, 300);
  return (
    <div className={`flex items-center justify-center p-6 ${full ? "min-h-screen bg-[#F6F8FC]" : "min-h-[60vh]"}`} data-testid="error-boundary">
      <div className="w-full max-w-md rounded-2xl border border-slate-100 bg-white p-6 text-center shadow-xl">
        <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-amber-50 text-amber-500"><AlertTriangle size={22} /></div>
        <p className="text-lg font-bold text-slate-900">Terjadi kesalahan</p>
        <p className="mt-1 text-sm text-slate-500">Halaman ini gagal ditampilkan. Data Anda aman — coba lagi atau muat ulang halaman.</p>
        {detail && <p className="mt-3 break-words rounded-lg bg-slate-50 p-2 text-left font-mono text-[11px] text-slate-400" data-testid="error-detail">{detail}</p>}
        <div className="mt-4 flex flex-wrap justify-center gap-2">
          {onRetry && <button onClick={onRetry} data-testid="error-retry" className="btn-primary"><RotateCcw size={14} /> Coba lagi</button>}
          {onHome && <button onClick={onHome} data-testid="error-home" className="btn-soft"><Home size={14} /> Ke Beranda</button>}
          <button onClick={() => window.location.reload()} data-testid="error-reload" className="btn-soft"><RefreshCw size={14} /> Muat ulang</button>
        </div>
      </div>
    </div>
  );
}

// Prevents a white screen: any render crash shows a friendly recovery card instead.
// `resetKey` (e.g. the route path) clears the error when it changes, so navigating away recovers without a reload.
export class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error, info) { console.error("UI crash:", error, info?.componentStack); }
  componentDidUpdate(prev) { if (this.state.error && prev.resetKey !== this.props.resetKey) this.setState({ error: null }); }
  render() {
    if (!this.state.error) return this.props.children;
    return <Fallback error={this.state.error} full={!this.props.inline} onRetry={() => this.setState({ error: null })} onHome={this.props.onHome} />;
  }
}

// Route-level boundary: keeps the app shell (sidebar, bell) alive when one page crashes; resets on navigation.
export function PageErrorBoundary({ children }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  return <ErrorBoundary inline resetKey={pathname} onHome={pathname === "/home" ? undefined : () => navigate("/home")}>{children}</ErrorBoundary>;
}
